import React from 'react';
import { Box, Typography, Paper, CircularProgress, Alert, Fade } from '@mui/material';
import IdeaCard from './IdeaCard';
import { Idea } from '../types';

interface IdeaListProps {
  ideas: Idea[];
  loading: boolean;
  error?: string;
  onUpvote: (ideaId: string) => void;
}

const IdeaList: React.FC<IdeaListProps> = ({ ideas, loading, error, onUpvote }) => {
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        my: 6,
        height: '200px'
      }}>
        <CircularProgress size={60} thickness={4} sx={{ 
          color: (theme) => `${theme.palette.secondary.main}`,
          animation: 'pulse 1.5s infinite ease-in-out'
        }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Fade in={true} timeout={800}>
        <Alert 
          severity="error" 
          sx={{ 
            my: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            '& .MuiAlert-icon': {
              fontSize: '1.5rem'
            }
          }}
        >
          {error}
        </Alert>
      </Fade>
    );
  }

  if (ideas.length === 0) {
    return (
      <Fade in={true} timeout={800}>
        <Paper sx={{ 
          p: 5, 
          textAlign: 'center', 
          my: 4,
          borderRadius: 2,
          boxShadow: '0 6px 16px rgba(0,0,0,0.05)',
          background: 'linear-gradient(to right, #f9f9f9, #ffffff)',
          border: '1px solid #f0f0f0',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '4px',
            background: (theme) => 
              `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
          }
        }}>
          <Typography variant="h5" color="text.primary" sx={{ mb: 2, fontWeight: 500 }}>
            No ideas found matching your criteria.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '500px', mx: 'auto' }}>
            Try adjusting your filters or be the first to submit an idea! Your contribution could shape the future of Severance Town Center.
          </Typography>
        </Paper>
      </Fade>
    );
  }

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        mx: -1.5,
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: -50,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '80%',
          height: '1px',
          background: 'linear-gradient(to right, rgba(0,0,0,0), rgba(0,0,0,0.1), rgba(0,0,0,0))'
        }
      }}
    >
      {ideas.map((idea, index) => (
        <Box 
          key={idea.id} 
          sx={{ 
            width: { xs: '100%', sm: '50%', md: '33.33%' }, 
            p: 1.5,
            height: 'fit-content'
          }}
        >
          <IdeaCard idea={idea} onUpvote={onUpvote} index={index} />
        </Box>
      ))}
    </Box>
  );
};

export default IdeaList; 